import {
  SayItsATable,
  SayItsATableBody,
  SayItsATableBodyCell,
  SayItsATableBodyRow,
  SayItsATableHeader,
} from "routes/Base/Plans/New/CreatePlan/MediaTypes/table"
import { WithAuthority } from "components/WithAuthority"
import Options from "components/inputs/Options"
import { TextInput } from "components/inputs/TextInput"
import IconButton from "@material-ui/core/IconButton"
import { ReactComponent as DeleteIcon } from "assets/delete-grey.svg"
import { css, cx } from "@emotion/css"
import { useI18n } from "providers/I18n"
import { operatorOptions } from "./index"
import { FormulaPart, MediaField } from "../../GlobalMediaTypes/types"
import { formatFloatingPointNumber } from "utils/number-format"
import { ErrorsMap } from "api/http/utils"

type Props = {
  disabled: boolean
  deleteRow: (_: React.MouseEvent<HTMLButtonElement>, index: number) => void
  idx: number
  formula: FormulaPart
  handleChange: (
    evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined,
  ) => void
  handleAttrChange: (
    evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined,
  ) => void
  mediaFields?: MediaField[]
  errors?: ErrorsMap
  disableDelete?: boolean
}

const COLUMNS = [
  { id: "leftAttribute", header: "admin.mediaFields.calculated.attribute" },
  { id: "operation", header: "admin.mediaFields.calculated.operation" },
  { id: "rightAttribute", header: "admin.mediaFields.calculated.attribute" },
  { id: "deleteButton", header: "", width: 48 },
] as const

function operandsOptions(t: (key: string) => string) {
  return [
    {
      label: t("admin.mediaFields.calculated.mediaFieldId"),
      value: "mediaFieldId",
    },
    {
      label: t("admin.mediaFields.calculated.fixedValue"),
      value: "fixedValue",
    },
  ]
}

export function FormulaRow(props: Props) {
  const { t } = useI18n()
  const {
    disabled,
    deleteRow,
    idx,
    formula,
    handleChange,
    handleAttrChange,
    mediaFields,
    errors,
    disableDelete,
  } = props

  const mediaFieldIds =
    mediaFields?.map((mf) => ({ label: mf?.name, value: mf?.id })) ?? []

  return (
    <SayItsATable
      className={cx(styles.table, {
        [styles.unauthorized]: disabled,
      })}
    >
      {idx === 0 ? <SayItsATableHeader columns={COLUMNS} /> : null}
      <SayItsATableBody>
        <SayItsATableBodyRow
          action={
            !disableDelete ? (
              <WithAuthority allowed={["AUTH_EDIT_MEDIA_FIELD"]}>
                <IconButton onClick={(e) => deleteRow(e, idx)}>
                  <DeleteIcon className={styles.deleteIcon} />
                </IconButton>
              </WithAuthority>
            ) : (
              ""
            )
          }
        >
          <SayItsATableBodyCell>
            {!disabled && (
              <Options
                name={`${idx}.leftOperand`}
                options={operandsOptions(t)}
                value={Object.keys(formula?.leftOperand ?? {})?.[0]}
                onChange={handleAttrChange}
                className={styles.cell}
                disabled={disabled}
                error={errors?.[`formulaSet.formulaParts[${idx}].leftOperand`]}
              />
            )}
            {!!formula?.leftOperand?.hasOwnProperty("mediaFieldId") ? (
              <Options
                name="leftOperand.mediaFieldId"
                options={mediaFieldIds}
                value={formula?.leftOperand?.mediaFieldId}
                onChange={handleChange}
                error={
                  errors?.[
                    `formulaSet.formulaParts[${idx}].leftOperand.mediaFieldId`
                  ]
                }
                disabled={disabled}
                className={cx({ [styles.disabled]: disabled })}
              />
            ) : (
              <TextInput
                name="leftOperand.fixedValue"
                value={formatFloatingPointNumber(2)(
                  formula?.leftOperand?.fixedValue || "",
                )}
                onChange={handleChange}
                error={
                  !!errors?.[
                    `formulaSet.formulaParts[${idx}].leftOperand.fixedValue`
                  ]
                }
                errorMessage={
                  errors?.[
                    `formulaSet.formulaParts[${idx}].leftOperand.fixedValue`
                  ]
                }
                className={cx({ [styles.disabled]: disabled })}
                disabled={disabled}
                fullWidth
              />
            )}
          </SayItsATableBodyCell>

          <SayItsATableBodyCell>
            <Options
              name="operator"
              options={operatorOptions(t)}
              value={formula?.operator}
              onChange={handleChange}
              error={errors?.[`formulaSet.formulaParts[${idx}].operator`]}
              paperClass={styles.options}
              disabled={disabled}
              className={cx({ [styles.disabled]: disabled })}
            />
          </SayItsATableBodyCell>

          <SayItsATableBodyCell>
            {!disabled && (
              <Options
                name={`${idx}.rightOperand`}
                options={operandsOptions(t)}
                value={Object.keys(formula?.rightOperand ?? {})?.[0]}
                onChange={handleAttrChange}
                className={styles.cell}
                disabled={disabled}
                error={errors?.[`formulaSet.formulaParts[${idx}].rightOperand`]}
              />
            )}
            {!!formula?.rightOperand?.hasOwnProperty("mediaFieldId") ? (
              <Options
                name="rightOperand.mediaFieldId"
                options={mediaFieldIds}
                value={formula?.rightOperand?.mediaFieldId}
                onChange={handleChange}
                error={
                  errors?.[
                    `formulaSet.formulaParts[${idx}].rightOperand.mediaFieldId`
                  ]
                }
                disabled={disabled}
                className={cx({ [styles.disabled]: disabled })}
              />
            ) : (
              <TextInput
                name="rightOperand.fixedValue"
                value={formatFloatingPointNumber(2)(
                  formula?.rightOperand?.fixedValue || "",
                )}
                onChange={handleChange}
                error={
                  !!errors?.[
                    `formulaSet.formulaParts[${idx}].rightOperand.fixedValue`
                  ]
                }
                errorMessage={
                  errors?.[
                    `formulaSet.formulaParts[${idx}].rightOperand.fixedValue`
                  ]
                }
                className={cx({ [styles.disabled]: disabled })}
                disabled={disabled}
                fullWidth
              />
            )}
          </SayItsATableBodyCell>
        </SayItsATableBodyRow>
      </SayItsATableBody>
    </SayItsATable>
  )
}

const styles = {
  table: css`
    padding-left: 10px;

    .wrap-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .row {
      grid-template-columns: 2fr 1fr 2fr;
    }

    .thc {
      align-items: center;
      justify-content: flex-start;
    }

    .tbr {
      height: 120px;
      outline: #f5f5f5 solid var(--spacing-xs);
      border: 1px solid var(--primary-main);
      border-radius: 0;
      width: 100%;
    }

    .cell {
      max-width: 324px;
      > div {
        > p {
          margin-left: 0;
          margin-right: 0;
          white-space: normal;
          padding-top: 5px;
        }
      }
    }

    .row-action {
      position: inherit;
      padding-left: 20px;
    }
  `,
  unauthorized: css`
    .tbr {
      height: var(--spacing-xxl-4);
    }
  `,
  deleteIcon: css`
    width: 24px;
    height: 24px;
    cursor: pointer;
    color: var(--grey);
  `,
  cell: css`
    margin-right: 10px;
  `,
  options: css`
    > ul > li {
      min-height: 38px;
    }
  `,
  disabled: css`
    .MuiInputBase-root.Mui-disabled {
      color: var(--black);
    }
    .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline {
      border: none;
    }
    .MuiSelect-icon.Mui-disabled {
      color: transparent;
    }
  `,
}
